import type { Brand, Locale } from "@borealis/layout-express";
import { formatNumber as borealisFormatNumber, type NumberFormatOptions } from "@borealis/number-format";
import type { ICarUsedAdView } from "@finn-no/adview-provider-frontend-types";

export const formatNumber = (number: number | undefined, locale: Locale, options?: NumberFormatOptions) => {
  if (number == null) {
    return null;
  } else {
    return borealisFormatNumber(number, locale, {
      maximumFractionDigits: 2,
      ...options,
    });
  }
};

/**
 * The mileage unit type is shared across multiple ad types, so we reuse the type from ICarUsedAdView.
 */
type MileageUnit = ICarUsedAdView["mileage_unit"];

export const formatMileage = (mileage: number, unit: MileageUnit, locale: string): string => {
  const formatterUnit = unit === "SCANDINAVIAN_MILE" ? "mile-scandinavian" : "kilometer";
  const formatter = new Intl.NumberFormat(locale, {
    style: "unit",
    unit: formatterUnit,
    useGrouping: true,
  });

  return formatter.format(mileage);
};

const localeToTimezone: Partial<Record<Locale, string>> = {
  nb: "Europe/Oslo",
  da: "Europe/Copenhagen",
  sv: "Europe/Stockholm",
};

export const toDateInputValue = (date: Date, locale: Locale): string => {
  // sv-SE produces the YYYY-MM-DD format required by <input type="date">
  const formatter = new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: localeToTimezone[locale] ?? "UTC",
  });
  return formatter.format(date);
};

const dateTimeFormatConfig = (locale?: Locale): Intl.DateTimeFormatOptions => {
  const config: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: (locale && localeToTimezone[locale]) ?? "UTC",
  };
  if (locale === "da") {
    config.month = "short";
  }
  return config;
};

/** Remember to use supressHydrationWarning in the component that uses this function */
export const formatDateTime = (dateTimeString?: string, locale?: Locale) => {
  if (dateTimeString == null) {
    return dateTimeString;
  } else {
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) {
      return dateTimeString;
    }
    try {
      const config = dateTimeFormatConfig(locale);
      const formatter = new Intl.DateTimeFormat(locale, config);

      const parts = formatter.formatToParts(date);
      let formattedDateTimeString = "";
      parts.forEach((part) => {
        if (part.type === "literal" && part.value.includes("kl.")) {
          formattedDateTimeString += " ";
          return;
        }
        formattedDateTimeString += part.value;
        if (part.type === "year" && config.month === "long") formattedDateTimeString += ",";
      });

      return formattedDateTimeString;
    } catch (error) {
      console.error("Error formatting date", error);
      return dateTimeString;
    }
  }
};

/** Remember to use supressHydrationWarning in the component that uses this function */
export const formatDate = (dateString?: string, locale?: Locale) => {
  if (dateString == null) {
    return null;
  } else {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    try {
      const formatter = new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      return formatter.format(date);
    } catch (error) {
      console.error("Error formatting date", error);
      return dateString;
    }
  }
};

/** Formats the brand name for display purposes. For example, "dba" becomes "DBA". */
export const formatBrand = (brand: Brand) => {
  switch (brand) {
    case "dba":
      return "DBA";
    case "finn":
      return "FINN";
    case "blocket":
      return "Blocket";
    case "bilbasen":
      return "Bilbasen";
    default:
      return brand;
  }
};
